'use strict';
/***
 * options.conversation {Conversation Model} (required) - conversation to which this message will be posted
 * options.attachmentPingInterval {number} - how often to ping for processing attachments (in milliseconds)
 */
var SoaModel = require('bunsen/models/SoaModel');
var extendCompose = require('bunsen/libraries/extendCompose');
module.exports = extendCompose.call(SoaModel);
var Backbone = require('dibs-backbone');
var _ = require('underscore');
var endpoints = require('dibs-endpoints');
var Anchor = require('bunsen/models/Anchor');
var Action = require('bunsen/models/ecomm/Action');
var MessageRecipient = require('bunsen/models/usercomm/MessageRecipient');
var AttachmentList = require('bunsen/collections/AttachmentList');
var Offer = require('bunsen/models/ecomm/Offer');
var ConversationMember = require('bunsen/models/usercomm/ConversationMember');
var copyText = require('copy-text')();
var defaultAttachmentPingInterval = 2000;
var Message = module.exports;
var globalDataLogger = require('bunsen/libraries/globalDataLogger');
var Deferred = require('simply-deferred').Deferred;

copyText = copyText.extend({
    noRecipients : "Your message has no recipients",
    emptyMessage : "Your message is empty"
});
module.exports.extendSelf({
    initialize : function (attributes, options) {
        this._conversation = options.conversation;
        this.options = _.extend({
            attachmentPollTimeout: 60000,
            attachmentPingInterval: defaultAttachmentPingInterval
        }, options);
        this._conversationMemberConstructor = this.options.conversationMemberConstructor || ConversationMember;
        if (this.options.action) {
            this.setAction(this.options.action);
        }
        this.on('change:viewState', this.startPollingIfAppropriate);
        this.startPollingIfAppropriate();
        return this.options;
    },
    startPollingIfAppropriate: function () {
        var viewState = this.get('viewState');
        if (viewState && 'expanded' !== viewState) {
            return;
        }
        this.off('change:viewState', this.startPollingIfAppropriate);
        this.gotAllAttachments = this.pollForAttachments();
        _.delay(function () {
            clearTimeout(this._pollforAttachmentsTimeout);
        }.bind(this), this.options.attachmentPollTimeout);
    },
    parse : function () {
        var data = Message.__super__.parse.apply(this, arguments);
        if ("string" === typeof data.isLatestMessageSender) {
            data.isLatestMessageSender = data.isLatestMessageSender === 'Y';
        }
        return data;
    },
    pollForAttachments: function () {
        var fetchOptions = {
            data: { fields: 'attachment'}
        };

        if (!this.get('attachmentCount')) {
            return Deferred().resolve();
        }
        if (!this.areAttachmentsProcessing()) {
            return Deferred().resolve();
        }
        return this.fetch(fetchOptions).then(function () {
            var pollComplete = Deferred();
            clearTimeout(this._pollforAttachmentsTimeout);
            this._pollforAttachmentsTimeout = _.delay(function () {
                this.pollForAttachments()
                    .fail(pollComplete.reject.bind(pollComplete))
                    .done(pollComplete.resolve.bind(pollComplete));
            }.bind(this), this.options.attachmentPingInterval);
            return pollComplete;
        }.bind(this));
    },
    areAttachmentsProcessing: function () {
        return !!this.get('attachments').where({processed: 'N'}).length;
    },
    idAttribute : 'id',
    url : function () {
        var conversation;
        if (this.id) {
            return endpoints.messageGET({message: this.id});
        }
        conversation = this.get('from.conversation');
        if (conversation && conversation.id) {
            return endpoints.messagePOST({conversation: conversation.id});
        }
        return endpoints.messageNewConversation();
        // TODO: throw error instead of returning messageNewConversation once contact dealer uses new ContactDealerMessage model
        //throw new Error("from.conversation.id must be set in order to create a message");
    },
    defaults : {
        attachments : [],
        recipients : []
    },
    relations: function () {
        return [
            {
                key : 'from',
                relatedModel : this._conversationMemberConstructor,
                type : Backbone.One
            },
            {
                key : 'recipients',
                relatedModel : MessageRecipient,
                type : Backbone.Many
            },
            {
                key: 'attachments',
                collectionType : AttachmentList,
                type: Backbone.Many
            },
            {
                key : 'emailDataMap',
                relatedModel : SoaModel,
                type : Backbone.One
            },
            {
                key : 'anchors',
                relatedModel : Anchor,
                type : Backbone.Many
            },
            {
                key: 'offer',
                relatedModel: Offer,
                type: Backbone.One,
                saveJSONFields: ['id', 'transaction']
            }, {
                type: Backbone.One,
                key: 'action',
                relatedModel: Action
            }
        ];
    },
    _conversationMemberConstructor: ConversationMember,
    validateMessageBody: function () {
        var trimmedBody = this.get('textBody') ? this.get('textBody').trim() : '';
        if (this.get('offer')) {
            return;
        }
        if (!trimmedBody) {
            return copyText.get('emptyMessage');
        }
    },
    validate: function () {
        if (this.get('recipients') && this.get('recipients').length === 0) {
            return copyText.get('noRecipients');
        }
        return this.validateMessageBody();
    },
    // prepare the message for a POST by infering any conspicuously missing required properties from the current running context
    preparedForPost : function (options) {
        options = _.extend({
            userId: undefined,
            masquerade: false
        }, options);
        var from = this.get('from');
        var meInTheconversation;
        if (!from) {
            meInTheconversation = this._conversationMemberConstructor.getMe(_.extend({
                userId: options.userId,
                conversation: this._conversation
            }, _.pick(options, 'masquerade')));
            this.set('from', meInTheconversation);
            from = meInTheconversation;
        }
        if (this.get('message')) {
            globalDataLogger.event({
                "detected message-in-a-message in Message.prototype.preparedForPost" : this.toJSON()
            });
        }
        var myUserId = from.get('user.id');
        var originalMembersThatArentMe = _.compact(this._conversation.get('members').map(function (member) {
            var memberUserId = member.get("user.id");
            if (memberUserId === myUserId) {
                return;
            }
            if (!memberUserId) {
                return;
            }
            if ('Y' !== member.get('isOriginal')) {
                return;
            }
            var recipientDetails = member.pick(
                'id',
                'userType' // only because this is set incorrectly in the MessageRecipient default
            );
            recipientDetails.user = {id: memberUserId};
            return new MessageRecipient({recipientDetails: recipientDetails});
        }));
        if (0 === originalMembersThatArentMe.length) {
            console.warn("message has no recipients");
        }
        this.get('recipients').add(originalMembersThatArentMe);
        this.set('subject', options.subject || _.chain(this._conversation.get('messages').pluck('subject')).compact().last().value());
        return Deferred().resolve(); // TODO: consider running the code from this method in initialize since it no longer runs asynchronously
    },
    flag: function (options) {
        options.messageId = this.get('id');
        return (new SoaModel()).save(options, { url: endpoints.flagMessage });
    },
    setAction: function (mcAction) {
        this.set('action', new Action({ name: mcAction }));
    },
    getConversation: function () {
        return this._conversation;
    },
    suppressEmails: function () {
        this.set('sendNotification', 'N');
    }
});

Object.assign(Message, {
    ReviewStatus: {
        open: 'OPEN',
        monitoring: 'MONITORING',
        inProgress: 'IN_PROGRESS',
        closed: 'CLOSED'
    },
    MC_ACTIONS: {
        contactDealer: 'CONTACT_DEALER',
        netPrice: 'NET_PRICE_REQUEST',
        mcReply: 'MC_REPLY',
        privateOffer: 'PRIVATE_OFFER',
        netPriceOffer: 'NET_PRICE_OFFER'
    }
});



//////////////////
// WEBPACK FOOTER
// ./~/bunsen/models/usercomm/Message.js
// module id = 72
// module chunks = 4 5 6 7 8 9 10 11 12 13 14 15 16 17 18 19 20 21 22 23 24 25 26 27 28 29 30 31 32 33 34 35 36 37 38 39 40 41 42 43 44 45 46 47 48 49 50 51 52 53 54